import { ArrowDown, ArrowUp, Enter, Escape } from '@fluentui/keyboard-keys'
import { Form } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'

import { isEmailValid } from '@/lib/string/string-validators'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Input } from '@/components/lemmala-design-system/input/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/lemmala-design-system/popover/popover'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { UserBadge } from '@/components/shared/user-badge/user-badge'

import { IndexedGroup, InputEmailsProps, Option } from './input-emails.types'
import { RecipientsSuggestionsList } from './recipients-suggestions-list'

export const InputEmails = ({
  onRecipientChange,
  placeholder = 'example@gmail.com',
  recipients,
  formProperty,
  container,
  suggestRecipientsGroups,
  excludeEmails,
}: InputEmailsProps) => {
  const [recipientInput, setRecipientInput] = useState('')
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState<number>(-1)
  const { toast } = useToast()
  const form = Form.useFormInstance()
  const inputRef = useRef<HTMLInputElement>(null)
  const ignoreNextOutsideRef = useRef(false)
  const suppressAutoOpenRef = useRef(false)

  const commitRecipients = ({
    nextRecipients,
    resetInput = true,
    closePopover = true,
  }: {
    nextRecipients: string[]
    resetInput?: boolean
    closePopover?: boolean
  }) => {
    onRecipientChange(nextRecipients)
    if (formProperty && form) {
      form.setFieldValue(formProperty, nextRecipients)
    }
    if (resetInput) {
      setRecipientInput('')
    }
    if (closePopover) {
      setIsPopoverOpen(false)
    }
  }

  const handleAddRecipient = () => {
    let recipient = recipientInput.trim()
    if (recipient.endsWith(',')) {
      recipient = recipient.slice(0, -1)
    }

    if (!isEmailValid(recipient)) {
      toast({
        variant: 'destructive',
        title: 'Invalid email address',
      })
      return
    }

    if (recipients.includes(recipient)) {
      toast({
        variant: 'destructive',
        title: 'Email address already added',
      })
      return
    }

    commitRecipients({ nextRecipients: [...recipients, recipient] })
  }

  const removeRecipient = (recipient: string) => {
    commitRecipients({
      nextRecipients: recipients.filter((existingRecipient) => existingRecipient !== recipient),
      resetInput: false,
      closePopover: false,
    })
  }

  const addManyRecipients = (emails: string[]) => {
    const validNewEmails = emails
      .map((email) => email.trim())
      .filter((email) => isEmailValid(email))
      .filter((email) => !recipients.includes(email))

    if (validNewEmails.length === 0) {
      return false
    }

    commitRecipients({ nextRecipients: [...recipients, ...validNewEmails] })
    return true
  }

  const selectSuggestedRecipients = (emails: string[]) => {
    suppressAutoOpenRef.current = true
    addManyRecipients(emails)
    setIsPopoverOpen(false)
  }

  const filteredGroups = useMemo(() => {
    return (suggestRecipientsGroups || []).flatMap((group) => {
      const q = recipientInput.trim().toLowerCase()
      if (q.length === 0) return [group]
      const groupNameMatches = group.groupName.toLowerCase().includes(q)
      const memberMatches = group.groupMembers.filter(
        (member) =>
          member.email.toLowerCase().includes(q) || (member.name || '').toLowerCase().includes(q),
      )
      if (groupNameMatches) return [{ ...group, groupMembers: group.groupMembers }]
      if (memberMatches.length > 0) return [{ ...group, groupMembers: memberMatches }]
      return []
    })
  }, [suggestRecipientsGroups, recipientInput])

  const displayedGroups = useMemo(() => {
    const listToExclude = [...recipients, ...(excludeEmails || [])]
    return filteredGroups
      .map((group) => ({
        ...group,
        groupMembers: group.groupMembers.filter((member) => !listToExclude.includes(member.email)),
      }))
      .filter((group) => group.groupMembers.length > 0)
  }, [filteredGroups, recipients, excludeEmails])

  const { options, indexedGroups } = useMemo(() => {
    const nextOptions: Option[] = []
    let runningIndex = -1
    const nextIndexedGroups: IndexedGroup[] = displayedGroups.map((group) => {
      const remainingToAdd = group.groupMembers.map((member) => member.email)
      nextOptions.push({ type: 'group', groupName: group.groupName, emails: remainingToAdd })
      const groupIdx = ++runningIndex
      const memberIdxs = group.groupMembers.map((member) => {
        nextOptions.push({ type: 'member', email: member.email, name: member.name })
        return ++runningIndex
      })
      return { group, groupIdx, memberIdxs }
    })

    return { options: nextOptions, indexedGroups: nextIndexedGroups }
  }, [displayedGroups])

  const didInitOptionsRef = useRef(false)
  useEffect(() => {
    if (didInitOptionsRef.current) {
      setHighlightIndex(-1)
    } else {
      didInitOptionsRef.current = true
    }
  }, [options])

  useEffect(() => {
    const isInputFocused = document.activeElement === inputRef.current
    if (!isInputFocused) return
    if (suppressAutoOpenRef.current) {
      setIsPopoverOpen(false)
      return
    }
    if (displayedGroups.length > 0) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsPopoverOpen(true)
    } else {
      setIsPopoverOpen(false)
    }
  }, [recipientInput, displayedGroups, inputRef])

  const moveHighlight = (direction: 1 | -1) => {
    if (!isPopoverOpen && options.length > 0) {
      setIsPopoverOpen(true)
    }
    if (options.length === 0) return
    setHighlightIndex((prev) => {
      const next =
        prev === -1
          ? direction === 1
            ? 0
            : options.length - 1
          : (prev + direction + options.length) % options.length
      return next
    })
  }

  const selectHighlighted = (): boolean => {
    if (highlightIndex < 0 || highlightIndex >= options.length) return false

    const highlightedOption = options[highlightIndex]
    if (highlightedOption.type === 'group') {
      selectSuggestedRecipients(highlightedOption.emails)
      return true
    }

    selectSuggestedRecipients([highlightedOption.email])
    return true
  }

  const haltEvent = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const isSuffixInteraction = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest('button'))

  return (
    <>
      <Popover
        open={isPopoverOpen && displayedGroups.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            if (ignoreNextOutsideRef.current) {
              ignoreNextOutsideRef.current = false
              return
            }
            if (document.activeElement === inputRef.current && displayedGroups.length > 0) {
              return
            }
          }
          setIsPopoverOpen(open)
        }}
      >
        <PopoverTrigger asChild>
          <div
            onMouseDown={(e) => {
              if (isSuffixInteraction(e.target)) return
              e.preventDefault()
              ignoreNextOutsideRef.current = true
            }}
            onMouseUp={(e) => {
              if (isSuffixInteraction(e.target)) return
              inputRef.current?.focus()
            }}
          >
            <Input
              ref={inputRef}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              suffix={
                <Button
                  disabled={!isEmailValid(recipientInput.trim())}
                  onClick={handleAddRecipient}
                  variant={'secondary'}
                  size={'sm'}
                >
                  Add
                </Button>
              }
              className='h-10 w-full'
              placeholder={placeholder}
              value={recipientInput}
              onChange={(e) => {
                suppressAutoOpenRef.current = false
                setRecipientInput(e.currentTarget.value)
              }}
              onFocus={() => {
                if (!suppressAutoOpenRef.current && displayedGroups.length > 0) {
                  setIsPopoverOpen(true)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === ArrowDown) {
                  haltEvent(e)
                  moveHighlight(1)
                  return
                }
                if (e.key === ArrowUp) {
                  haltEvent(e)
                  moveHighlight(-1)
                  return
                }
                if (e.key === Enter) {
                  if (isPopoverOpen) {
                    const handled = selectHighlighted()
                    if (handled) {
                      haltEvent(e)
                      return
                    }
                  }
                  handleAddRecipient()
                  e.stopPropagation()
                  return
                }
                if (e.key === Escape) {
                  if (isPopoverOpen) {
                    setIsPopoverOpen(false)
                    haltEvent(e)
                  }
                }
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className='w-[var(--radix-popover-trigger-width)] rounded border bg-main-background-card p-0'
          align='start'
          container={container?.current ?? undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {displayedGroups.length > 0 && (
            <RecipientsSuggestionsList
              highlightedIndex={highlightIndex}
              indexedGroups={indexedGroups}
              onSelectGroup={selectSuggestedRecipients}
              onSelectMember={(email) => selectSuggestedRecipients([email])}
            />
          )}
        </PopoverContent>
      </Popover>
      <div className='flex flex-wrap gap-2 pt-2'>
        {recipients?.map((recipient) => (
          <UserBadge
            key={recipient}
            tooltipDisabled
            onRemove={() => removeRecipient(recipient)}
            name={recipient}
          />
        ))}
      </div>
    </>
  )
}
